import { cn } from "@lib/cn";
import { useEffect, useRef } from "react";
import { GradientEditor } from "./gradient/gradient-editor";
import { useGradientState } from "./hooks/use-gradient-state";
import { gradientFromSolid } from "./lib/defaults";
import { isGradient } from "./lib/detect";
import { parseGradient } from "./lib/parse";
import { serializeGradient } from "./lib/serialize";
import SolidEditor from "./solid-editor";

export interface ColorPickerProps {
	current?: string;
	onColorChange?: (color: string) => void;
	onColorAdd?: (color: string) => void;
	onGoBack?: () => void;
	className?: string;
	disableGradient?: boolean;
	/** Force the picker to open in a specific mode. */
	initialMode?: "solid" | "gradient";
	/** Theme color swatches to show in the gradient stop editor. */
	themeColors?: { key: string; tooltip: string }[];
	/** Custom solid colors to show in the gradient stop editor. */
	customSolidColors?: string[];
	/** When true, the save button says "Save changes" instead of "Use color" / "Add gradient". */
	isEditing?: boolean;
}

const DEBOUNCE_MS = 250;

/**
 * ColorPicker container orchestrating Solid and Gradient editing modes.
 *
 * Mode is determined by `initialMode` prop (or auto-detected from `current`).
 * No tabs — the parent controls which mode to open.
 *
 * NOTE: `current` is snapshotted at mount time only. If the caller needs the
 * picker to reset when `current` changes (e.g. undo, remote edit), it must
 * pass `key={current}` to force a remount.
 */
const ColorPicker = ({
	current,
	onColorChange,
	onColorAdd,
	onGoBack,
	className,
	disableGradient,
	initialMode,
	themeColors,
	customSolidColors,
	isEditing,
}: ColorPickerProps) => {
	const initialCurrentRef = useRef(current);
	const initialCurrent = initialCurrentRef.current;
	const currentIsGradient = !!(initialCurrent && isGradient(initialCurrent));
	const initialGradient =
		initialCurrent && isGradient(initialCurrent)
			? parseGradient(initialCurrent)
			: initialCurrent
				? gradientFromSolid(initialCurrent)
				: null;

	const mode: "solid" | "gradient" = disableGradient
		? "solid"
		: (initialMode ?? (currentIsGradient ? "gradient" : "solid"));

	const {
		state: gradientState,
		dispatch,
		css: gradientCss,
		canAddStop,
		canRemoveStop,
	} = useGradientState(initialGradient ?? undefined);

	// Debounced onColorChange emission for gradient mode.
	// Flush on unmount so the last edit is not lost if the popover closes mid-debounce.
	const pendingCssRef = useRef<string | null>(null);
	useEffect(() => {
		if (mode !== "gradient" || !onColorChange) return;
		pendingCssRef.current = gradientCss;
		const timer = setTimeout(() => {
			onColorChange(gradientCss);
			pendingCssRef.current = null;
		}, DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [mode, gradientCss, onColorChange]);

	useEffect(() => {
		return () => {
			if (pendingCssRef.current != null && onColorChange) {
				onColorChange(pendingCssRef.current);
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className={cn(className)}>
			{mode === "solid" ? (
				<SolidEditor
					current={currentIsGradient ? undefined : current}
					onColorChange={onColorChange}
					onColorAdd={onColorAdd}
					onGoBack={onGoBack}
					isEditing={isEditing}
				/>
			) : (
				<div className="tw-w-full tw-px-4 tw-pb-4 tw-pt-4">
					<GradientEditor
						state={gradientState}
						dispatch={dispatch}
						css={gradientCss}
						canAddStop={canAddStop}
						canRemoveStop={canRemoveStop}
						themeColors={themeColors}
						customSolidColors={customSolidColors}
						onAddGradient={
							onColorAdd
								? () => onColorAdd(serializeGradient(gradientState.gradient))
								: undefined
						}
						onGoBack={onGoBack}
						isEditing={isEditing}
					/>
				</div>
			)}
		</div>
	);
};

export default ColorPicker;
